import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import { msgErrore } from '../utils/formati';
import { RefreshCw, Rocket, Clock, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';

// Pagina Deploy (solo staging): pubblica in produzione la versione provata in staging
// tramite /deploy/* (script deploy-produzione sul server).

const INTERVALLO_IN_CORSO = 5000;
const MAX_ATTESA = 15 * 60 * 1000;   // dopo "Pubblica ora" si smette comunque di aggiornare

const card = { background: 'white', border: '1px solid #e8eaed', borderRadius: 10, padding: '16px 18px', marginBottom: 16 };
const titoloCard = { fontSize: 11, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10, fontWeight: 600 };
const hash = { fontFamily: 'monospace', background: '#F5F5F4', padding: '2px 6px', borderRadius: 4 };
const pulsante = (principale, disabilitato) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 6, fontSize: 14,
  fontWeight: 600, cursor: disabilitato ? 'not-allowed' : 'pointer', opacity: disabilitato ? 0.5 : 1,
  border: principale ? '1px solid #2B6CB0' : '1px solid #d6d3d1',
  background: principale ? '#2B6CB0' : 'white', color: principale ? 'white' : '#1A1A2E',
});

function Indicatore({ titolo, attivo, nota, evidenza }) {
  const colore = attivo ? (evidenza ? '#C2410C' : '#2B6CB0') : '#a0aec0';
  return (
    <div style={{ flex: 1, minWidth: 180, border: `1px solid ${attivo && evidenza ? '#FDBA74' : '#e8eaed'}`,
      background: attivo && evidenza ? '#FFF7ED' : 'white', borderRadius: 8, padding: '10px 14px',
      borderTop: `3px solid ${colore}` }}>
      <div style={{ fontWeight: 600, color: '#1A1A2E' }}>{titolo}: {attivo ? 'Sì' : 'No'}</div>
      {attivo && nota && <div style={{ fontSize: 12, color: '#57534e', marginTop: 4 }}>{nota}</div>}
    </div>
  );
}

export default function Deploy() {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [avviso, setAvviso] = useState(null);
  const [occupato, setOccupato] = useState(false);
  const [seguendo, setSeguendo] = useState(null);   // { ultimo, da } dopo "Pubblica ora"
  const [logAperto, setLogAperto] = useState(false);
  const [righeLog, setRigheLog] = useState([]);
  const logApertoRef = useRef(false);
  logApertoRef.current = logAperto;

  const caricaLog = useCallback(async () => {
    try {
      const r = await API.get('/deploy/log', { params: { righe: 200 } });
      setRigheLog(r.data.righe || []);
    } catch (e) {
      setRigheLog([msgErrore(e, 'Impossibile leggere il log del deploy')]);
    }
  }, []);

  const carica = useCallback(async () => {
    try {
      const r = await API.get('/deploy/anteprima');
      setDati(r.data);
      setErrore(null);
    } catch (e) {
      setErrore(msgErrore(e, "Impossibile leggere lo stato del deploy"));
    }
    if (logApertoRef.current) caricaLog();
  }, [caricaLog]);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { if (logAperto) caricaLog(); }, [logAperto, caricaLog]);

  // Dopo "Pubblica ora" si segue finché il deploy non è finito e c'è un esito nuovo
  useEffect(() => {
    if (!seguendo || !dati) return;
    const finito = !dati.in_corso && dati.richiesta?.modalita !== 'immediato'
      && JSON.stringify(dati.ultimo) !== seguendo.ultimo;
    if (finito || Date.now() - seguendo.da > MAX_ATTESA) setSeguendo(null);
  }, [dati, seguendo]);

  const inCorso = !!dati && (dati.in_corso || dati.richiesta?.modalita === 'immediato' || !!seguendo);

  useEffect(() => {
    if (!inCorso) return undefined;
    const t = setInterval(carica, INTERVALLO_IN_CORSO);
    return () => clearInterval(t);
  }, [inCorso, carica]);

  const esegui = async (percorso, dopo) => {
    setOccupato(true); setErrore(null); setAvviso(null);
    try {
      const r = await API.post(`/deploy/${percorso}`);
      setAvviso(r.data.messaggio);
      if (dopo) dopo();
    } catch (e) {
      setErrore(msgErrore(e, 'Operazione non riuscita'));
    } finally {
      setOccupato(false);
      carica();
    }
  };

  const pubblicaOra = () => {
    let testo = `Pubblicare subito in produzione ${dati.staging}?`;
    if (dati.backend) testo += '\n\nCambia il backend: gli utenti della produzione perdono la connessione per qualche secondo.';
    if (dati.migrazioni.length) testo += `\n\nVerranno applicate le migrazioni: ${dati.migrazioni.join(', ')}`;
    if (!window.confirm(testo)) return;
    const ultimo = JSON.stringify(dati.ultimo);
    esegui('avvia', () => setSeguendo({ ultimo, da: Date.now() }));
  };

  if (!dati) {
    return (
      <div style={{ padding: 24, maxWidth: 1100 }}>
        <h2 style={{ marginTop: 0 }}>Deploy in produzione</h2>
        {errore ? <div style={{ color: '#B91C1C' }}>{errore}</div> : <div style={{ color: '#78716c' }}>Caricamento…</div>}
      </div>
    );
  }

  const prenotazione = dati.richiesta?.modalita === 'prenotato' ? dati.richiesta : null;
  const disabilitato = dati.bloccato || inCorso || occupato;
  const ultimoOk = dati.ultimo?.esito === 'ok';

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Deploy in produzione</h2>
        <button onClick={carica} style={{ ...pulsante(false, false), padding: '5px 10px', fontSize: 13, marginLeft: 'auto' }}>
          <RefreshCw size={14} /> Aggiorna
        </button>
      </div>

      <div style={{ fontSize: 16, marginBottom: 16 }}>
        Produzione <span style={hash}>{dati.produzione}</span> → Staging <span style={hash}>{dati.staging || '—'}</span>
      </div>

      {dati.bloccato && (
        <div style={{ ...card, background: '#FFF7ED', border: '1px solid #FDBA74', color: '#9A3412', display: 'flex', gap: 10 }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>{dati.motivo}</div>
        </div>
      )}

      {errore && <div style={{ ...card, background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#B91C1C' }}>{errore}</div>}
      {avviso && !errore && <div style={{ ...card, background: '#F0FDF4', border: '1px solid #86EFAC', color: '#166534' }}>{avviso}</div>}

      {inCorso && (
        <div style={{ ...card, background: '#EFF6FF', border: '1px solid #93C5FD', color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: 10 }}>
          <RefreshCw size={16} />
          <strong>In corso</strong> — la pagina si aggiorna ogni 5 secondi finché il deploy non è finito.
        </div>
      )}

      {dati.commit.length > 0 && (
        <div style={card}>
          <div style={titoloCard}>Commit in uscita ({dati.commit.length})</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {dati.commit.map(c => (
                <tr key={c.hash} style={{ borderTop: '1px solid #f5f5f4' }}>
                  <td style={{ padding: '7px 8px 7px 0', whiteSpace: 'nowrap' }}><span style={hash}>{c.hash}</span></td>
                  <td style={{ padding: '7px 8px', width: '100%' }}>{c.messaggio}</td>
                  <td style={{ padding: '7px 8px', whiteSpace: 'nowrap', color: '#57534e' }}>{c.autore}</td>
                  <td style={{ padding: '7px 0 7px 8px', whiteSpace: 'nowrap', color: '#78716c' }}>{c.data}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            <Indicatore titolo="Backend" attivo={dati.backend} nota="Comporta un restart del servizio di produzione" />
            <Indicatore titolo="Frontend" attivo={dati.frontend} nota="Nuova build dell'interfaccia" />
            <Indicatore titolo="Migrazioni" attivo={dati.migrazioni.length > 0} evidenza
              nota={dati.migrazioni.map(m => m.replace(/^migrations\//, '')).join(', ')} />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button style={pulsante(true, disabilitato)} disabled={disabilitato} onClick={() => esegui('prenota')}>
          <Clock size={16} /> Prenota alle 20:00
        </button>
        <button style={pulsante(false, disabilitato)} disabled={disabilitato} onClick={pubblicaOra}>
          <Rocket size={16} /> Pubblica ora
        </button>
      </div>

      {prenotazione && (
        <div style={{ ...card, borderTop: '3px solid #2B6CB0' }}>
          <div style={titoloCard}>Prenotazione delle 20:00</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span>Commit <span style={hash}>{(prenotazione.commit || '').slice(0, 7)}</span></span>
            <span>da <strong>{prenotazione.utente}</strong></span>
            <span style={{ color: '#78716c' }}>il {prenotazione.creata_il}</span>
            <button style={{ ...pulsante(false, occupato), marginLeft: 'auto' }} disabled={occupato}
              onClick={() => { if (window.confirm('Annullare la prenotazione delle 20:00?')) esegui('annulla'); }}>
              Annulla prenotazione
            </button>
          </div>
        </div>
      )}

      {dati.ultimo && (
        <div style={{ ...card, background: ultimoOk ? '#F0FDF4' : '#FEF2F2',
          border: `1px solid ${ultimoOk ? '#86EFAC' : '#FCA5A5'}`, color: ultimoOk ? '#166534' : '#B91C1C' }}>
          <div style={{ ...titoloCard, color: 'inherit' }}>Ultimo deploy: {ultimoOk ? 'riuscito' : 'errore'}</div>
          <div>{dati.ultimo.messaggio}</div>
          <div style={{ fontSize: 12, marginTop: 6, color: '#57534e' }}>
            <span style={hash}>{dati.ultimo.da}</span> → <span style={hash}>{dati.ultimo.a}</span>
            {' · '}{dati.ultimo.utente}{' · '}{dati.ultimo.data}
          </div>
        </div>
      )}

      <div style={card}>
        <button onClick={() => setLogAperto(a => !a)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, ...titoloCard, marginBottom: 0 }}>
          {logAperto ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Log del deploy
        </button>
        {logAperto && (
          <pre style={{ marginTop: 12, marginBottom: 0, maxHeight: 400, overflow: 'auto', background: '#1A1A2E', color: '#E2E8F0',
            padding: 12, borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {righeLog.length ? righeLog.join('\n') : 'Log vuoto'}
          </pre>
        )}
      </div>
    </div>
  );
}
